import { FC, useRef, useState } from 'react';
import { UploadIcon, XIcon } from '@heroicons/react/outline';
import { useUploadProviderProgramMediaMutation } from '../../../generated/graphql';
import {
  allAcceptedFileExtensions,
  MAX_UPLOAD_SIZE_BYTES,
  MAX_UPLOAD_SIZE_MB,
} from '../../lib/media';
import { getFileTypeFromName, getFileTypeIcon } from '../../lib/fileUtils';
import Spinner from '../../svgs/Spinner';

export interface ProviderFileUploadForAssessmentProps {
  programId: string;
  questionIndex: number;
  question: string;
  isRequired: boolean;
  value?: { mediaId: string; fileName: string } | null;
  onChange: (value: { mediaId: string; fileName: string } | null) => void;
  disabled?: boolean;
  allowedExtensions?: string[] | null;
}

export const ProviderFileUploadForAssessment: FC<
  ProviderFileUploadForAssessmentProps
> = ({
  programId,
  questionIndex,
  question,
  isRequired,
  value,
  onChange,
  disabled = false,
  allowedExtensions = [],
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadProviderProgramMedia, { loading: isUploading }] =
    useUploadProviderProgramMediaMutation();
  const [error, setError] = useState<string | null>(null);

  const accept =
    allowedExtensions && allowedExtensions.length > 0
      ? allowedExtensions.join(',')
      : allAcceptedFileExtensions.join(',');

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      setError(
        `File is too large. Please upload a file smaller than ${MAX_UPLOAD_SIZE_MB}MB.`,
      );
      e.target.value = '';
      return;
    }

    try {
      const result = await uploadProviderProgramMedia({
        variables: {
          input: {
            programId,
            file,
            name: file.name,
          },
        },
      });

      const mediaId = result.data?.uploadProviderProgramMedia?.media?.id;
      const fileName =
        result.data?.uploadProviderProgramMedia?.name ?? file.name;

      if (mediaId) {
        onChange({ mediaId, fileName });
        if (inputRef.current) inputRef.current.value = '';
      } else {
        setError(
          'Upload succeeded but file data was incomplete. Please try again.',
        );
      }
    } catch {
      setError('Failed to upload file. Please try again.');
    }
    e.target.value = '';
  };

  const handleRemove = () => {
    onChange(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleButtonClick = () => inputRef.current?.click();

  if (!programId) {
    return (
      <p className="text-caption text-neutral-200">
        Please select a client from the dropdown in order to upload a file. The
        file will appear in the client&apos;s Documents & Files section.
      </p>
    );
  }

  return (
    <div id={`question-${questionIndex}`} className="flex flex-col space-y-3">
      {value ? (
        <div className="flex items-center justify-between rounded-md border border-neutral-100 bg-neutral-25 px-4 py-3">
          <div className="flex items-center space-x-2">
            {(() => {
              const fileType = getFileTypeFromName(value.fileName);
              const FileTypeIcon = getFileTypeIcon(fileType);
              return <FileTypeIcon className="h-5 w-5 text-neutral-125" />;
            })()}
            <span className="text-caption text-neutral-150">
              {value.fileName}
            </span>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleRemove}
              className="text-neutral-100 hover:text-red-100"
              aria-label="Remove file"
            >
              <XIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={handleButtonClick}
            disabled={disabled || isUploading}
            className="flex items-center gap-2 rounded-lg border border-neutral-100 bg-white px-3 py-2 text-caption text-neutral-150 hover:bg-neutral-25 disabled:opacity-60"
          >
            {isUploading ? (
              <Spinner className="h-5 w-5 shrink-0" />
            ) : (
              <UploadIcon className="h-5 w-5 shrink-0" />
            )}
            <span>{isUploading ? 'Uploading...' : 'Upload a file'}</span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
            aria-hidden
          />
          <p className="text-small-caption text-neutral-125">
            The file will be added to the client&apos;s Documents & Files
            section. Max {MAX_UPLOAD_SIZE_MB}MB.
          </p>
        </>
      )}

      {error && (
        <span className="text-small-caption text-red-100">{error}</span>
      )}

      {isRequired && !value && (
        <span className="text-small-caption text-neutral-125">* Required</span>
      )}
    </div>
  );
};
